import { Button, TextButton } from '@creditornot/cb-button';
import { Checkbox } from '@creditornot/cb-checkbox';
import { CaretDown, MagnifyingGlass } from '@creditornot/cb-icons';
import { Typography as T } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { Popover } from '@creditornot/cb-popover';
import React, { ChangeEvent, FC, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import useDebounceString from 'common/hooks/useDebounceString';

type Props = {
  availableCities: string[];
  selectedCities: string[];
  handleSelect: (cityList: string[]) => void;
};

const CityFilter: FC<Props> = ({ availableCities, selectedCities, handleSelect }) => {
  const intl = useIntl();
  return (
    <Popover
      content={
        <div data-testid="city-filter-popover">
          <CityFilterPopover
            availableCities={availableCities}
            selectedCities={selectedCities}
            handleSelect={handleSelect}
          />
        </div>
      }
      showOnClick
      showOnHover={false}
      renderArrow={false}
      placement="bottom"
    >
      <Button
        variant={selectedCities.length > 0 ? 'lightBlue' : 'white'}
        size="small"
        icon={<CaretDown />}
        iconPosition="right"
      >
        {selectedCities.length > 0 ? (
          <T.Body3>{selectedCities.join(',')}</T.Body3>
        ) : (
          <T.Body3>{intl.formatMessage({ id: 'venue-availability-status.city' })}</T.Body3>
        )}
      </Button>
    </Popover>
  );
};

const PopoverWrapper = styled.div`
  width: 240px;
  padding-inline: 8px;
`;

const PopoverTitle = styled(T.Small)`
  color: ${({ theme }) => theme.cbSemanticColors.textSecondary};
`;
const CitiesContainer = styled.div`
  border-block-end: 1px solid ${({ theme }) => theme.cbSemanticColors.borderPrimary};
  margin-block: 14px 8px;
  max-height: 340px;
  overflow: scroll;
`;
const StyledCheckbox = styled(Checkbox)`
  margin-block-end: 8px;
`;
const SelectAllContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-block-end: 4px;
`;

const StyledSearchInput = styled(Input)`
  margin-block: 8px;
  width: 100%;
`;

type PopoverProps = {
  availableCities: string[];
  selectedCities: string[];
  handleSelect: (cityList: string[]) => void;
};
const CityFilterPopover: FC<PopoverProps> = ({ availableCities, selectedCities, handleSelect }) => {
  const intl = useIntl();
  const [searchString, setSearchString] = useState('');
  const debouncedSearchString = useDebounceString(searchString);

  const filteredCities = useMemo(() => {
    const containsSearchString = (city: string) => {
      if (debouncedSearchString === '') {
        return true;
      }
      const searchString = debouncedSearchString.toLowerCase();
      return city.toLowerCase() === searchString;
    };
    return availableCities.filter(containsSearchString);
  }, [availableCities, debouncedSearchString]);

  const handleSelectAll = () => handleSelect([...availableCities]);
  const handleDeselectAll = () => handleSelect([]);
  const handleCheckboxChange = (city: string) => (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      handleSelect([...selectedCities, city]);
    } else {
      handleSelect(selectedCities.filter(selectedCity => selectedCity !== city));
    }
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => setSearchString(e.target.value);
  return (
    <PopoverWrapper>
      <PopoverTitle>
        {intl.formatMessage({ id: 'venue-availability-status.filters.city-dropdown-header' })}
      </PopoverTitle>
      {availableCities.length > 10 && (
        <StyledSearchInput
          icon={<MagnifyingGlass />}
          placeholder={intl.formatMessage({ id: 'navigation.search' })}
          type="text"
          value={searchString}
          onChange={handleSearch}
          onClearClick={() => setSearchString('')}
        />
      )}
      <CitiesContainer>
        {filteredCities.map(city => (
          <StyledCheckbox key={city} checked={selectedCities.includes(city)} onChange={handleCheckboxChange(city)}>
            {city}
          </StyledCheckbox>
        ))}
      </CitiesContainer>
      <SelectAllContainer>
        {availableCities.length > selectedCities.length && (
          <TextButton size="small" onClick={handleSelectAll}>
            <FormattedMessage id="venue-availability-status.filters.select-all" />
          </TextButton>
        )}
        {selectedCities.length > 0 && (
          <TextButton size="small" onClick={handleDeselectAll} variant="red">
            <FormattedMessage id="venue-availability-status.filters.deselect-all" />
          </TextButton>
        )}
      </SelectAllContainer>
    </PopoverWrapper>
  );
};

export default CityFilter;
